/**
 * The shape warhol's function tools return, read on BOTH sides of the
 * christo/carrie boundary.
 *
 * This module exists because the two readers used to live apart -- one in
 * `services/christo/app/handlers/catalyst/warholChat.ts`, one in
 * `frontends/carrie/src/routes/ask-puntt/components/truncatedJson.ts` -- and
 * their drifting apart is exactly what let a failed reviewer render as a
 * finished one. The contract belongs in one place, not copy-pasted either side
 * of the service/frontend line.
 *
 * The shapes, from warhol's own source:
 *
 * - `BudgetInResults` (`services/warhol/agents/budget.py`) merges a `budget`
 *   key into EVERY function-tool result, so a reader that does not take it
 *   back off is reading a different document than the tool returned.
 * - A MAPPING result just gains the key. A LIST result cannot carry one, so
 *   `with_budget` wraps it as `{result: [...], budget: {...}}` -- which is what
 *   `run_subagents` returns (a list of `SubagentReport`) on every real turn.
 */
// why: `without_budget` (`services/warhol/contracts/budget.py`) is the Python
// twin of `withoutWarholBudget` below; its docstring is the spec: "Anything
// without the key is handed back untouched, so a reader may call this on every
// return."
export const WARHOL_BUDGET_KEY = "budget";
export const WARHOL_RESULT_KEY = "result";

/**
 * christo's cap on a stored warhol tool-result summary.
 *
 * It lives here, beside the reader, because carrie has to assume it: a fan-out
 * whose report JSON exceeds this arrives as a cut-off document, and the cut
 * falls mid-JSON (and, because `budget` serializes AFTER the reports, almost
 * always removes that key too).
 */
export const WARHOL_STEP_SUMMARY_CHARS = 500;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * A warhol tool return as the tool made it, with the budget note taken back off.
 *
 * A mapping result just gains the key; a list result -- and `run_subagents`
 * returns its reports as a list -- cannot take one, so `with_budget` wraps it
 * as `{result: [...], budget: {...}}`. Anything without a `budget` key comes
 * back untouched, so a caller may apply it to every return.
 */
export function withoutWarholBudget(value: unknown): unknown {
  if (!isRecord(value) || !(WARHOL_BUDGET_KEY in value)) return value;
  const keys = Object.keys(value);
  if (keys.length === 2 && WARHOL_RESULT_KEY in value) {
    return value[WARHOL_RESULT_KEY];
  }
  const rest: Record<string, unknown> = { ...value };
  delete rest[WARHOL_BUDGET_KEY];
  return rest;
}

/**
 * The report list in a `run_subagents` return, or null for anything else.
 *
 * The reports come back wrapped as `{result: [...], budget: {...}}`
 * (`withoutWarholBudget`), and this accepts the array under `result` whether
 * or not the note is still beside it -- carrie reads the SAME return through a
 * CAPPED summary, where the trailing `budget` key is already cut off, so keying
 * the unwrap on that key being present unwraps nothing on the real payload.
 * A bare list (limits unbound) is taken as-is.
 */
export function fanOutReports(parsed: unknown): unknown[] | null {
  const unwrapped = withoutWarholBudget(parsed);
  if (Array.isArray(unwrapped)) return unwrapped;
  if (isRecord(unwrapped) && Array.isArray(unwrapped[WARHOL_RESULT_KEY])) {
    return unwrapped[WARHOL_RESULT_KEY] as unknown[];
  }
  return null;
}
